import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import TimeBadge from "@/components/TimeBadge";
import { useAuth } from "@/contexts/AuthContext";
import { CAUSE_COLORS, CAUSE_NAMES } from "@/lib/causes";
import { Search, MapPin, Globe, Plus, Filter, Sparkles, LogIn } from "lucide-react";

export default function Opportunities() {
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const [params, setParams] = useSearchParams();
  const [opps, setOpps] = useState([]);
  const [causes, setCauses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const cause = params.get("cause") || "all";
  const mode = params.get("mode") || "all";
  const matchedOnly = params.get("matched") === "1";

  useEffect(() => {
    api.get("/causes").then(({ data }) => setCauses(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (cause !== "all") q.set("cause", cause);
    if (mode !== "all") q.set("mode", mode);
    if (search) q.set("search", search);
    if (matchedOnly && isAuthed && user?.id) {
      q.set("matched_only", "true");
      q.set("user_id", user.id);
    }
    api.get(`/opportunities?${q.toString()}`).then(({ data }) => {
      setOpps(data);
      setLoading(false);
    });
  }, [cause, mode, search, matchedOnly, isAuthed, user?.id]);

  const updateFilter = (key, val) => {
    const next = new URLSearchParams(params);
    if (val === "all" || !val) next.delete(key);
    else next.set(key, val);
    setParams(next);
  };

  return (
    <Layout>
      <div data-testid="opportunities-page" className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
          <div>
            <span className="overline text-[var(--cm-primary)]">Volunteer</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 tracking-tight">
              Find your next shift.
            </h1>
            <p className="text-ink-soft mt-2 max-w-xl">
              Every shift earns you Time Hours — once the organizer verifies you showed up.
            </p>
          </div>
          {isAuthed ? (
            <Link to="/opportunities/new" data-testid="create-opportunity-btn" className="btn-primary inline-flex items-center gap-2">
              <Plus className="h-4 w-4" /> Post an opportunity
            </Link>
          ) : (
            <Link
              to="/register"
              data-testid="guest-cta-register"
              className="btn-primary inline-flex items-center gap-2"
            >
              <LogIn className="h-4 w-4" /> Sign up to volunteer
            </Link>
          )}
        </div>

        {!isAuthed && (
          <div
            data-testid="opps-guest-banner"
            className="card-soft p-4 mb-6 flex flex-wrap items-center justify-between gap-3 border-l-4"
            style={{ borderLeftColor: "var(--cm-primary)" }}
          >
            <p className="text-sm text-ink-soft">
              Browsing as a guest — <span className="text-ink font-semibold">create a free account</span> to sign up, post opportunities, and earn Time Hours.
            </p>
            <Link to="/login" className="text-sm font-semibold text-[var(--cm-primary)] underline underline-offset-4" data-testid="opps-guest-signin">
              Sign in
            </Link>
          </div>
        )}

        {/* Filters */}
        <div className="card-soft p-5 grid md:grid-cols-12 gap-3 items-center mb-8">
          <div className="md:col-span-5 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
            <input
              data-testid="opps-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search opportunities…"
              className="w-full pl-10 pr-4 py-2.5 bg-app rounded-xl border border-ink-soft focus:ring-2 focus:ring-[var(--cm-primary)]/40"
            />
          </div>
          <div className="md:col-span-3">
            <select
              data-testid="opps-cause-filter"
              value={cause}
              onChange={(e) => updateFilter("cause", e.target.value)}
              className="w-full px-3 py-2.5 bg-app rounded-xl border border-ink-soft"
            >
              <option value="all">All causes</option>
              {causes.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <select
              data-testid="opps-mode-filter"
              value={mode}
              onChange={(e) => updateFilter("mode", e.target.value)}
              className="w-full px-3 py-2.5 bg-app rounded-xl border border-ink-soft"
            >
              <option value="all">Online & local</option>
              <option value="online">Online only</option>
              <option value="local">Local only</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <button
              data-testid="opps-matched-toggle"
              onClick={() => updateFilter("matched", matchedOnly ? null : "1")}
              disabled={!isAuthed}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                matchedOnly
                  ? "bg-[var(--cm-ink)] text-white border-[var(--cm-ink)]"
                  : "bg-app border-ink-soft hover:border-[var(--cm-primary)]"
              }`}
              title={!isAuthed ? "Sign in to see your matched opportunities" : ""}
            >
              <Sparkles className="h-3.5 w-3.5" />
              {isAuthed ? (user?.primary_cause ? "My matches" : "Match me") : "Sign in to match"}
            </button>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <p className="text-ink-soft" data-testid="opps-loading">Loading opportunities…</p>
        ) : opps.length === 0 ? (
          <div className="card-soft p-12 text-center" data-testid="opps-empty">
            <Filter className="h-6 w-6 mx-auto text-ink-soft" />
            <p className="font-display text-xl mt-3 font-semibold">Nothing matches that.</p>
            <p className="text-ink-soft mt-1">Try clearing a filter or broadening your search.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {opps.map((o) => (
              <Link
                key={o.id}
                to={`/opportunities/${o.id}`}
                data-testid={`opp-card-${o.id}`}
                className="card-soft p-6 hover:-translate-y-0.5 transition-transform group"
              >
                <div className="flex items-start justify-between mb-3">
                  <span
                    className="px-2.5 py-1 rounded-full text-xs font-semibold text-white"
                    style={{ background: CAUSE_COLORS[o.cause] }}
                  >
                    {CAUSE_NAMES[o.cause]}
                  </span>
                  <TimeBadge hours={o.hours} size="sm" />
                </div>
                <h3 className="font-display font-semibold text-lg leading-tight group-hover:text-[var(--cm-primary)] transition-colors">
                  {o.title}
                </h3>
                <p className="text-sm text-ink-soft mt-2 line-clamp-3">{o.description}</p>
                <div className="mt-4 flex items-center gap-3 text-xs text-ink-soft">
                  {o.mode === "online" ? (
                    <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" /> Online</span>
                  ) : (
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {o.location}</span>
                  )}
                  {o.is_ongoing ? (
                    <span className="text-[var(--cm-primary)] font-semibold">· Ongoing</span>
                  ) : (
                    o.date && <span>· {o.date}</span>
                  )}
                  <span className="ml-auto">
                    {o.signups_count} signed up
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
